import { useEffect, useMemo, useState } from "react"
import { QueryClient, useMutation, useQuery } from "react-query"

import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EFeatureFlags,
  TCreatePaymentSheet,
  TEventId,
  TLeaveNonTicketedEvent,
  TTicketData,
} from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import { useSetRecoilState } from "recoil"

import { eventAtom } from "@fuse/recoil/events"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import {
  addTicketsApi,
  claimTicketForEvent,
  createPaymentSheetApi,
  deleteTicketApi,
  getAllEvents,
  getAllTicketsApi,
  getEventDetails,
  joinEvent,
  joinWaitlistForEvent,
  leaveEventApi,
  leaveNonTicketedEventApi,
  searchEvents,
} from "@api/firebaseWrappedApis"

const queryClient = new QueryClient()

export const useEventDetails = (eventId: string, uid?: string) => {
  return useQuery(["eventDetails", eventId, uid], () =>
    getEventDetails({ eventId, uid }),
  )
}

export const useGetAllTickets = (eventId: string) => {
  return useQuery(
    ["getAllTickets", eventId],
    () => getAllTicketsApi({ eventId }),
    {
      enabled: !!eventId,
      refetchOnWindowFocus: false,
    },
  )
}

export const useAddTicket = () => {
  return useMutation(
    (payload: { eventId: string; ticketData: TTicketData }) => {
      if (!payload.eventId) {
        return Promise.reject(new Error("Provide event id"))
      }
      return addTicketsApi(payload)
    },
  )
}

export const useDeleteTicket = () => {
  return useMutation((payload: { eventId: string; ticketId: string }) => {
    if (!payload.eventId) {
      return Promise.reject(new Error("Provide event id"))
    }
    return deleteTicketApi(payload)
  })
}

export const useCreatePaymentSheet = () => {
  return useMutation((payload: TCreatePaymentSheet) => {
    if (isEmpty(payload)) {
      // Handle empty payload scenario
      return Promise.reject(new Error("Payload cannot be empty"))
    }
    return createPaymentSheetApi(payload)
  })
}

export const useLeaveEvent = () => {
  return useMutation((payload: TEventId) => {
    if (isEmpty(payload)) {
      // Handle empty payload scenario
      return Promise.reject(new Error("payload cannot be empty"))
    }
    return leaveEventApi(payload)
  })
}

interface IJoinEvent extends TEventId {
  answersToQuestions?: any
}

export const useJoinEvent = (invitedId?: string, externalEventUrl?: string) => {
  return useMutation((payload: IJoinEvent) => {
    if (isEmpty(payload)) {
      // Handle empty payload scenario
      return Promise.reject(new Error("payload cannot be empty"))
    }

    const eventId = get(payload, "eventId", null)
    if (!eventId) {
      return Promise.reject(new Error("eventId is required"))
    }

    const numberOfExtraJoinee = get(payload, "numberOfExtraJoinee", 0)
    const answersToQuestions = get(payload, "answersToQuestions", [])

    if (externalEventUrl) {
      // Open in new tab
      window.open(externalEventUrl, "_blank", "noopener,noreferrer")
    }

    const data: {
      eventId: string
      invitedId?: string
      questionnaires?: Record<string, any>
      numberOfExtraJoinee?: number
    } = { eventId }

    if (invitedId) data.invitedId = invitedId
    if (!isEmpty(answersToQuestions))
      data.questionnaires = { ...answersToQuestions }
    if (numberOfExtraJoinee) data.numberOfExtraJoinee = numberOfExtraJoinee

    return joinEvent(data)
  })
}

export const useLeaveNonTicketedEvent = () => {
  return useMutation((payload: TLeaveNonTicketedEvent) => {
    if (isEmpty(payload)) {
      // Handle empty payload scenario
      return Promise.reject(new Error("payload cannot be empty"))
    }
    return leaveNonTicketedEventApi(payload)
  })
}

export const usePrimaryEvents = (uid?: string) => {
  const setEventAtom = useSetRecoilState(eventAtom)

  return useQuery(
    ["events", uid],
    () => getAllEvents({ uid }), // Fetch events
    {
      onSuccess: (data) => {
        // Update Recoil atom state with fetched data
        const responseData = get(data, "data", {})
        setEventAtom({
          userCreatedOpenEvents:
            get(responseData, "userCreatedOpenEvents", []) || [],
          userCreatedClosedEvents:
            get(responseData, "userCreatedClosedEvents", []) || [],
          userInvitedOpenEvents:
            get(responseData, "userInvitedOpenEvents", []) || [],
          userInvitedClosedEvents:
            get(responseData, "userInvitedClosedEvents", []) || [],
          coHostedEvents: get(responseData, "coHostedEvents", []) || [],
        })
      },
    },
  )
}

export const useJoinWaitlistForEvent = (payload: {
  eventId: string
  uid: string
}) => {
  return useMutation(
    () => {
      return joinWaitlistForEvent({
        eventId: payload.eventId,
      })
    },
    {
      onSuccess: (data) => {
        const response = data
        if (
          typeof response == "object" &&
          "success" in response &&
          "message" in response &&
          response.success === false
        ) {
          const messageInResp = response?.message as string
          showErrorToast(messageInResp)
        } else {
          showSuccessToast("You have joined the waiting list")
          queryClient.invalidateQueries([
            "eventDetails",
            payload?.eventId,
            payload?.uid,
          ])
        }
      },
      onError: (error) => {
        console.error(error)
        showErrorToast("Something went wrong")
      },
    },
  )
}

type TQueryBody = Partial<{
  location: {
    lat: number
    lon: number
  }
  searchTerm: string
  tags: string[]
  offset: number
  pageSize: number
}>

const shouldSearchBeEnable = ({ queryBody }: { queryBody: TQueryBody }) => {
  if (queryBody?.location?.lat && queryBody?.location?.lon) {
    return true
  } else if (queryBody?.searchTerm) {
    return true
  } else if (queryBody?.tags?.length) {
    return true
  } else {
    return false
  }
}

export const useSearchedEvent = (
  queryBody: TQueryBody,
  isEnabled: boolean = true,
  key: string,
) => {
  const [searchedResult, setSearchedResult] = useState<any[]>([])
  const [totalHits, setTotalHits] = useState<number>(0) // To track total available results

  useEffect(() => {
    if (get(queryBody, "offset", 0) <= 0) {
      setSearchedResult([])
    }
  }, [queryBody.offset])

  const includeExternalEvents = useProcessFeatureFlags(
    EFeatureFlags.EXPLORE_SECTION,
  )

  const {
    data,
    isFetching,
    isLoading,
    isError,
    refetch: refetchByTerm,
  } = useQuery({
    queryFn: () =>
      searchEvents({
        queryBody: {
          ...queryBody,
          includeExternalEvents: includeExternalEvents,
          key,
        },
      }),
    queryKey: ["SEARCHED_TERM", key, JSON.stringify(queryBody)],
    refetchOnWindowFocus: false,
    enabled: isEnabled && shouldSearchBeEnable({ queryBody }),
    onSuccess(data: { success: boolean; data: any[]; totalHits: number }) {
      setSearchedResult((prev) =>
        queryBody.offset ? [...prev, ...get(data, "data", [])] : data?.data,
      )
      setTotalHits(data?.totalHits || 0)
    },
  })

  return {
    searchedResult: searchedResult,
    isLoading: isFetching || isLoading,
    totalHits: totalHits, // Return total hits for pagination control
    refetchByTerm, // Expose refetch for manual trigger
    isError,
  }
}

export const useClaimTicketForEvent = (eventId: string) => {
  return useQuery(["claimTicket", eventId], () =>
    claimTicketForEvent({ eventId }),
  )
}
